import { CircleAlert, Info, X } from "lucide-react";
import type { CSSProperties, JSX, ReactNode } from "react";
import { cx } from "../cx";
import { TurnstileMark } from "./VerifiedLine";

export type ToastTone = "verified" | "info" | "error";

export interface ToastProps {
  /** Top-border accent; `verified` uses volt and the turnstile mark. */
  tone?: ToastTone | undefined;
  title?: string | undefined;
  children?: ReactNode | undefined;
  onDismiss?: (() => void) | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Notification toast — 360px card with a top-border tone accent. Announced
 * as a status region; errors announce assertively. Ported from
 * design/tarski-kit/components/overlay/Toast.jsx.
 */
export function Toast({
  tone = "info",
  title,
  children,
  onDismiss,
  className,
  style,
}: ToastProps): JSX.Element {
  const icon =
    tone === "verified" ? (
      <TurnstileMark size={14} />
    ) : tone === "error" ? (
      <CircleAlert size={16} aria-hidden="true" />
    ) : (
      <Info size={16} aria-hidden="true" />
    );
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cx("tk-toast", `tk-toast--${tone}`, className)}
      style={style}
    >
      <span className="tk-toast__icon" aria-hidden="true">
        {icon}
      </span>
      <div className="tk-toast__content">
        {title === undefined ? null : <div className="tk-toast__title">{title}</div>}
        <div className="tk-toast__message">{children}</div>
      </div>
      {onDismiss === undefined ? null : (
        <button
          type="button"
          className="tk-toast__dismiss"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
